import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE, NAV } from "@/lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleNav = (to) => {
    if (to.startsWith("/#")) {
      const id = to.replace("/#", "");
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 80);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-200 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      {/* Top utility strip */}
      <div className="hidden md:block bg-[#0A1128] text-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between text-xs font-mono-tactical tracking-widest py-2">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-2 text-emerald-400">
              <span className="w-2 h-2 bg-emerald-400 rounded-full pulse-dot" />
              SYS // OPERATIONAL
            </span>
            <span className="text-slate-500">|</span>
            <span className="text-slate-400">{SITE.address}</span>
          </div>
          <div className="flex items-center gap-4">
            {SITE.phones.map((p, i) => (
              <a key={p} href={`tel:${SITE.phonesRaw[i]}`} className="hover:text-white transition-colors" data-testid={`topbar-phone-${i}`}>
                {p}
              </a>
            ))}
            <span className="text-slate-500">|</span>
            <a href={`mailto:${SITE.email}`} className="hover:text-white transition-colors" data-testid="topbar-email">
              {SITE.email}
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <div className="relative w-11 h-11 border border-slate-300 flex items-center justify-center bg-white">
            <span className="absolute -top-1 -left-1 w-2 h-2 border-t border-l border-red-600" />
            <span className="absolute -top-1 -right-1 w-2 h-2 border-t border-r border-red-600" />
            <span className="absolute -bottom-1 -left-1 w-2 h-2 border-b border-l border-red-600" />
            <span className="absolute -bottom-1 -right-1 w-2 h-2 border-b border-r border-red-600" />
            <span className="font-display font-black text-[#0A1128] text-lg tracking-tighter">M</span>
            <span className="font-display font-black text-red-600 text-lg tracking-tighter">K</span>
          </div>
          <div className="leading-none">
            <div className="font-display font-black text-[#0A1128] text-base tracking-tight uppercase">
              Master King <span className="text-red-600">Corps</span>
            </div>
            <div className="overline mt-1 text-slate-500">{SITE.tagline}</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <button
              key={item.to}
              onClick={() => handleNav(item.to)}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="text-sm font-medium tracking-wide uppercase text-slate-700 hover:text-[#0A1128] transition-colors relative group"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-red-600 transition-all duration-200 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            data-testid="header-cta"
            onClick={() => handleNav("/#contact")}
            className="hidden sm:inline-flex bg-red-600 hover:bg-red-700 text-white rounded-none font-bold tracking-wide uppercase text-xs px-6 h-11"
          >
            <Phone className="w-3.5 h-3.5 mr-2" /> Request Quote
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 border border-slate-300 flex items-center justify-center text-[#0A1128] bg-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <button
                key={item.to}
                onClick={() => handleNav(item.to)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="flex items-center justify-between py-3 px-4 border border-transparent hover:border-slate-200 hover:bg-slate-50 text-[#0A1128] uppercase tracking-wide font-medium text-sm"
              >
                {item.label} <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
            <a
              href={`tel:${SITE.phonesRaw[0]}`}
              className="mt-4 inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white py-3 font-bold tracking-wide uppercase text-sm"
              data-testid="mobile-call-cta"
            >
              <Phone className="w-4 h-4 mr-2" /> {SITE.phones[0]}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
